import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { schedulingApi, usersApi } from '@/api';
import { Appointment, Agreement } from '@/types';
import { Modal, Field, Input, Select, Button, Badge, EmptyState, Skeleton } from '@/ui';
import { AgreementSendPanel } from '@/components/AgreementSendPanel';

const STATUS_TONE: Record<string, 'brand' | 'success' | 'neutral' | 'warning'> = {
  scheduled: 'warning',
  confirmed: 'success',
  completed: 'neutral',
  cancelled: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  scheduled: 'Unconfirmed',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

interface AppointmentFormData {
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  propertyAddress: string;
  scheduledDate: string;
  scheduledTime: string;
  duration: number;
}

const emptyForm: AppointmentFormData = {
  clientName: '',
  clientEmail: '',
  clientPhone: '',
  propertyAddress: '',
  scheduledDate: '',
  scheduledTime: '09:00',
  duration: 120,
};

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = (date: Date | string) => {
  return new Date(date).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDuration = (minutes: number) => {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

// Returns the most informative badge to show on the card for a given appointment.
// Agreement status takes priority over raw appointment status once an agreement exists.
type AgreementBadge = { tone: 'brand' | 'success' | 'neutral' | 'warning' | 'info'; label: string } | null;

const agreementBadge = (appt: Appointment): AgreementBadge => {
  if (!appt.agreementId) return null;
  if (appt.agreementStatus === 'signed') return { tone: 'success', label: 'Signed ✓' };
  if (appt.agreementStatus === 'expired') return { tone: 'warning', label: '⚠ Unsigned' };
  if (appt.agreementStatus === 'pending') return { tone: 'info', label: 'Agreement Sent' };
  return null;
};

const Scheduling: React.FC = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [defaultDuration, setDefaultDuration] = useState(120);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [formData, setFormData] = useState<AppointmentFormData>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState<string | null>(null);

  // Detail / confirm flow
  const [detailAppointment, setDetailAppointment] = useState<Appointment | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirmSuccess, setConfirmSuccess] = useState(false);

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      const [apptResp, profileResp] = await Promise.all([
        schedulingApi.getAppointments(),
        usersApi.getProfile(),
      ]);
      if (apptResp.success && apptResp.data) {
        setAppointments(apptResp.data);
        const openId = new URLSearchParams(window.location.search).get('open');
        if (openId) {
          const target = apptResp.data.find(a => a.id === openId);
          if (target) { setDetailAppointment(target); setConfirmError(null); setConfirmSuccess(false); }
        }
      }
      if (profileResp.success && profileResp.data?.defaultDuration) {
        setDefaultDuration(profileResp.data.defaultDuration);
      }
    } catch {
      console.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingAppointment(null);
    setFormData({ ...emptyForm, duration: defaultDuration });
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (appt: Appointment) => {
    setEditingAppointment(appt);
    const d = new Date(appt.scheduledDate);
    setFormData({
      clientName: appt.clientName,
      clientEmail: appt.clientEmail,
      clientPhone: appt.clientPhone || '',
      propertyAddress: appt.propertyAddress,
      scheduledDate: d.toISOString().slice(0, 10),
      scheduledTime: d.toTimeString().slice(0, 5),
      duration: appt.duration,
    });
    setError(null);
    setShowModal(true);
  };

  const openDetail = (appt: Appointment) => {
    setDetailAppointment(appt);
    setConfirmError(null);
    setConfirmSuccess(false);
  };

  const closeDetail = () => {
    setDetailAppointment(null);
    setConfirmError(null);
    setConfirmSuccess(false);
  };

  const applyAgreementToAppointment = (appointmentId: string, agreement: Agreement) => {
    setAppointments(prev =>
      prev.map(a =>
        a.id === appointmentId
          ? {
              ...a,
              agreementId: agreement.id,
              agreementStatus: agreement.status,
              agreementSentAt: agreement.sentAt,
              agreementSignedAt: agreement.signedAt,
              agreementSignedPdfUrl: agreement.signedPdfUrl,
            }
          : a,
      ),
    );
    setDetailAppointment(prev =>
      prev && prev.id === appointmentId
        ? {
            ...prev,
            agreementId: agreement.id,
            agreementStatus: agreement.status,
            agreementSentAt: agreement.sentAt,
            agreementSignedAt: agreement.signedAt,
            agreementSignedPdfUrl: agreement.signedPdfUrl,
          }
        : prev,
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'duration' ? parseInt(value, 10) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.clientName || !formData.propertyAddress || !formData.scheduledDate) {
      setError('Client name, property address, and date are required.');
      return;
    }

    const scheduledDate = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);

    setSubmitting(true);
    try {
      if (editingAppointment) {
        const response = await schedulingApi.updateAppointment(editingAppointment.id, {
          clientName: formData.clientName,
          clientEmail: formData.clientEmail,
          clientPhone: formData.clientPhone || undefined,
          propertyAddress: formData.propertyAddress,
          scheduledDate,
          duration: formData.duration,
        });
        if (response.success && response.data) {
          setAppointments(prev => prev.map(a => a.id === editingAppointment.id ? response.data! : a));
        }
      } else {
        const response = await schedulingApi.createAppointment({
          clientName: formData.clientName,
          clientEmail: formData.clientEmail,
          clientPhone: formData.clientPhone || undefined,
          propertyAddress: formData.propertyAddress,
          scheduledDate,
          duration: formData.duration,
        });
        if (response.success && response.data) {
          setAppointments(prev => [response.data!, ...prev]);
        }
      }
      setShowModal(false);
    } catch {
      setError(`Failed to ${editingAppointment ? 'update' : 'create'} appointment.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await schedulingApi.cancelAppointment(id);
      setAppointments(prev => prev.filter(a => a.id !== id));
      setCancelConfirm(null);
      if (detailAppointment?.id === id) closeDetail();
    } catch {
      console.error('Failed to cancel appointment');
    }
  };

  const handleConfirm = async (appt: Appointment) => {
    setConfirming(true);
    setConfirmError(null);
    setConfirmSuccess(false);
    try {
      const res = await schedulingApi.confirmAppointment(appt.id);
      if (res.success && res.data) {
        const { agreement, inspection } = res.data;
        const updatedAppt: Appointment = {
          ...appt,
          status: 'confirmed',
          inspectionId: inspection.id,
          agreementId: agreement.id,
          agreementStatus: 'pending',
          agreementSentAt: agreement.sentAt,
        };
        setAppointments(prev => prev.map(a => a.id === appt.id ? updatedAppt : a));
        setDetailAppointment(updatedAppt);
        setConfirmSuccess(true);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      const msg = axiosErr?.response?.data?.error
        ?? 'Failed to confirm appointment. Ensure you have an agreement template in Settings → Agreement.';
      setConfirmError(msg);
    } finally {
      setConfirming(false);
    }
  };

  // Sort: upcoming first (by date ascending), past at the bottom
  const sorted = [...appointments].sort(
    (a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime()
  );
  const now = new Date();
  const upcoming = sorted.filter(a => new Date(a.scheduledDate) >= now && a.status !== 'cancelled');
  const past = sorted.filter(a => new Date(a.scheduledDate) < now || a.status === 'cancelled');

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-display text-display-sm text-ink">Scheduling</h1>
        <Button variant="primary" onClick={openCreateModal} leadingIcon={<PlusIcon />}>
          New Appointment
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card flex items-center gap-4 p-4">
              <div className="flex-shrink-0 w-14 flex flex-col items-center gap-1">
                <Skeleton width="w-8" height="h-7" />
                <Skeleton width="w-10" height="h-3" />
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <Skeleton width="w-3/5" height="h-4" />
                <Skeleton width="w-2/5" height="h-3" />
                <Skeleton width="w-1/3" height="h-3" />
              </div>
              <Skeleton width="w-20" height="h-6" shape="pill" />
            </div>
          ))}
        </div>
      ) : appointments.length === 0 ? (
        <EmptyState
          title="No appointments yet"
          description="Schedule your first inspection to get started."
          action={
            <Button variant="primary" onClick={openCreateModal} leadingIcon={<PlusIcon />}>
              Schedule Your First Inspection
            </Button>
          }
        />
      ) : (
        <div className="space-y-6">
          {/* Upcoming */}
          {upcoming.length > 0 && (
            <div>
              <h2 className="text-overline text-ink-subtle mb-3">
                Upcoming ({upcoming.length})
              </h2>
              <div className="space-y-3">
                {upcoming.map(appt => (
                  <AppointmentRow
                    key={appt.id}
                    appointment={appt}
                    onView={() => openDetail(appt)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Past / Cancelled */}
          {past.length > 0 && (
            <div>
              <h2 className="text-overline text-ink-subtle mb-3">
                Past ({past.length})
              </h2>
              <div className="space-y-3 opacity-75">
                {past.map(appt => (
                  <AppointmentRow
                    key={appt.id}
                    appointment={appt}
                    onView={() => openDetail(appt)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingAppointment ? 'Edit Appointment' : 'New Appointment'}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={(e) => handleSubmit(e as unknown as React.FormEvent)}
              loading={submitting}
            >
              {editingAppointment ? 'Save Changes' : 'Create Appointment'}
            </Button>
          </>
        }
      >
        {error && (
          <div className="mb-4 p-3 bg-sev-major-soft text-sev-major-ink border border-sev-major/20 rounded-md text-body-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label="Property Address" htmlFor="appt-address">
            <Input
              id="appt-address"
              name="propertyAddress"
              value={formData.propertyAddress}
              onChange={handleChange}
              placeholder="123 Main St, City, State ZIP"
              required
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Client Name" htmlFor="appt-client-name">
              <Input
                id="appt-client-name"
                name="clientName"
                value={formData.clientName}
                onChange={handleChange}
                required
              />
            </Field>
            <Field label="Client Email" htmlFor="appt-client-email" optional>
              <Input
                id="appt-client-email"
                type="email"
                name="clientEmail"
                value={formData.clientEmail}
                onChange={handleChange}
              />
            </Field>
          </div>

          <Field label="Client Phone" htmlFor="appt-client-phone" optional>
            <Input
              id="appt-client-phone"
              type="tel"
              name="clientPhone"
              value={formData.clientPhone}
              onChange={handleChange}
            />
          </Field>

          <div className="grid grid-cols-3 gap-4">
            <Field label="Date" htmlFor="appt-date">
              <Input
                id="appt-date"
                type="date"
                name="scheduledDate"
                value={formData.scheduledDate}
                onChange={handleChange}
                required
              />
            </Field>
            <Field label="Time" htmlFor="appt-time">
              <Input
                id="appt-time"
                type="time"
                name="scheduledTime"
                value={formData.scheduledTime}
                onChange={handleChange}
              />
            </Field>
            <Field label="Duration" htmlFor="appt-duration">
              <Select id="appt-duration" name="duration" value={formData.duration} onChange={handleChange}>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
                <option value={150}>2.5 hours</option>
                <option value={180}>3 hours</option>
                <option value={210}>3.5 hours</option>
                <option value={240}>4 hours</option>
                <option value={270}>4.5 hours</option>
                <option value={300}>5 hours</option>
                <option value={330}>5.5 hours</option>
                <option value={360}>6 hours</option>
              </Select>
            </Field>
          </div>

          <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
        </form>
      </Modal>

      {/* Appointment Detail Modal */}
      {detailAppointment && (
        <AppointmentDetailModal
          appointment={detailAppointment}
          confirming={confirming}
          confirmError={confirmError}
          confirmSuccess={confirmSuccess}
          cancelConfirm={cancelConfirm}
          onClose={closeDetail}
          onEdit={() => { closeDetail(); openEditModal(detailAppointment); }}
          onConfirm={() => handleConfirm(detailAppointment)}
          onAgreementSent={(agreement) =>
            applyAgreementToAppointment(detailAppointment.id, agreement)
          }
          onCancelClick={() => setCancelConfirm(detailAppointment.id)}
          onCancelConfirm={() => handleCancel(detailAppointment.id)}
          onCancelDismiss={() => setCancelConfirm(null)}
          onViewInspection={(inspectionId) => { closeDetail(); navigate(`/inspections/${inspectionId}`); }}
        />
      )}
    </div>
  );
};

const PlusIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

// ─── AppointmentRow ──────────────────────────────────────────────────────────

interface AppointmentRowProps {
  appointment: Appointment;
  onView: () => void;
}

const AppointmentRow: React.FC<AppointmentRowProps> = ({ appointment, onView }) => {
  const agBadge = agreementBadge(appointment);
  return (
    <div
      className="card flex items-center justify-between p-4 cursor-pointer hover:bg-surface-subtle transition-colors"
      onClick={onView}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        {/* Date block */}
        <div className="flex-shrink-0 w-14 text-center">
          <div className="text-heading-lg font-semibold text-ink nums-tabular">
            {new Date(appointment.scheduledDate).getDate()}
          </div>
          <div className="text-caption text-ink-subtle uppercase tracking-wide">
            {new Date(appointment.scheduledDate).toLocaleDateString('en-US', { month: 'short' })}
          </div>
        </div>

        {/* Details */}
        <div className="min-w-0 flex-1">
          <div className="text-body-sm font-medium text-ink truncate">
            {appointment.propertyAddress}
          </div>
          <div className="text-body-sm text-ink-muted">
            {appointment.clientName}
            {appointment.clientEmail && <span className="text-ink-faint"> &middot; {appointment.clientEmail}</span>}
          </div>
          <div className="text-caption text-ink-subtle mt-0.5">
            {formatDate(appointment.scheduledDate)} at {formatTime(appointment.scheduledDate)}
            {' '}&middot; {formatDuration(appointment.duration)}
          </div>
        </div>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-2 ml-4">
        <Badge tone={STATUS_TONE[appointment.status] || 'neutral'} compact>
          {STATUS_LABEL[appointment.status] ?? appointment.status}
        </Badge>
        {agBadge && (
          <Badge tone={agBadge.tone} compact>
            {agBadge.label}
          </Badge>
        )}
      </div>
    </div>
  );
};

// ─── AppointmentDetailModal ──────────────────────────────────────────────────

interface AppointmentDetailModalProps {
  appointment: Appointment;
  confirming: boolean;
  confirmError: string | null;
  confirmSuccess: boolean;
  cancelConfirm: string | null;
  onClose: () => void;
  onEdit: () => void;
  onConfirm: () => void;
  onAgreementSent: (agreement: Agreement) => void;
  onCancelClick: () => void;
  onCancelConfirm: () => void;
  onCancelDismiss: () => void;
  onViewInspection: (inspectionId: string) => void;
}

const AppointmentDetailModal: React.FC<AppointmentDetailModalProps> = ({
  appointment,
  confirming,
  confirmError,
  confirmSuccess,
  cancelConfirm,
  onClose,
  onEdit,
  onConfirm,
  onAgreementSent,
  onCancelClick,
  onCancelConfirm,
  onCancelDismiss,
  onViewInspection,
}) => {
  // "Confirm & Send Agreement": for unconfirmed appointments with no agreement yet
  const canConfirm =
    appointment.status === 'scheduled' &&
    !appointment.agreementId &&
    !!appointment.clientEmail;

  // The inline send/resend action lives inside <AgreementSendPanel>; the
  // footer keeps only the multi-step "Confirm & Send Agreement" entry
  // point for unconfirmed appointments so the cancelled-state and
  // canConfirm gating stays here where it belongs.
  const agreementSnapshot = appointment.agreementId && appointment.agreementStatus
    ? {
        id: appointment.agreementId,
        status: appointment.agreementStatus,
        sentAt: appointment.agreementSentAt,
        signedAt: appointment.agreementSignedAt,
        signedPdfUrl: appointment.agreementSignedPdfUrl,
      }
    : null;
  // Hide the panel's inline action while the parent intends to use the
  // footer Confirm & Send Agreement button (the two would be redundant)
  // and while the appointment is cancelled (no resends on dead rows).
  const hidePanelAction = canConfirm || appointment.status === 'cancelled';

  return (
    <Modal
      open
      onClose={onClose}
      title="Appointment Details"
      size="md"
      footer={
        <div className="flex items-center justify-between w-full gap-3 flex-wrap">
          {/* Left side: cancel */}
          <div>
            {appointment.status !== 'cancelled' && appointment.status !== 'completed' && (
              cancelConfirm === appointment.id ? (
                <div className="flex items-center gap-2">
                  <span className="text-body-sm text-ink-muted">Cancel this appointment?</span>
                  <button
                    onClick={onCancelConfirm}
                    className="text-body-sm text-sev-major hover:text-sev-critical-ink transition-colors font-medium"
                  >
                    Yes, Cancel
                  </button>
                  <button
                    onClick={onCancelDismiss}
                    className="text-body-sm text-ink-subtle hover:text-ink-muted transition-colors"
                  >
                    Keep
                  </button>
                </div>
              ) : (
                <button
                  onClick={onCancelClick}
                  className="text-body-sm text-sev-major hover:text-sev-critical-ink transition-colors"
                >
                  Cancel Appointment
                </button>
              )
            )}
          </div>

          {/* Right side: edit + confirm */}
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="secondary" onClick={onEdit} size="sm">
              Edit
            </Button>
            {canConfirm && (
              <Button variant="primary" onClick={onConfirm} loading={confirming} size="sm">
                Confirm & Send Agreement
              </Button>
            )}
          </div>
        </div>
      }
    >
      {/* Property */}
      <div className="mb-5">
        <p className="text-body-xs text-ink-subtle font-medium uppercase tracking-wide mb-0.5">Property</p>
        <p className="text-body-md font-medium text-ink">{appointment.propertyAddress}</p>
      </div>

      {/* Client info */}
      <div className="mb-5">
        <p className="text-body-xs text-ink-subtle font-medium uppercase tracking-wide mb-1">Client</p>
        <p className="text-body-sm font-medium text-ink">{appointment.clientName}</p>
        {appointment.clientEmail && (
          <a
            href={`mailto:${appointment.clientEmail}`}
            className="text-body-sm text-brand hover:underline"
          >
            {appointment.clientEmail}
          </a>
        )}
        {!appointment.clientEmail && (
          <p className="text-body-sm text-ink-subtle italic">No email on file</p>
        )}
        {appointment.clientPhone && (
          <p className="text-body-sm text-ink-muted mt-0.5">
            <a href={`tel:${appointment.clientPhone}`} className="hover:underline">
              {appointment.clientPhone}
            </a>
          </p>
        )}
      </div>

      {/* Date / time */}
      <div className="mb-5">
        <p className="text-body-xs text-ink-subtle font-medium uppercase tracking-wide mb-1">Scheduled</p>
        <p className="text-body-sm text-ink">
          {formatDate(appointment.scheduledDate)} at {formatTime(appointment.scheduledDate)}
        </p>
        <p className="text-body-sm text-ink-muted">
          Duration: {formatDuration(appointment.duration)}
        </p>
      </div>

      {/* Agreement status */}
      <div className="mb-2">
        <p className="text-body-xs text-ink-subtle font-medium uppercase tracking-wide mb-1">Agreement</p>
        <AgreementSendPanel
          appointmentId={appointment.id}
          clientEmail={appointment.clientEmail || null}
          agreement={agreementSnapshot}
          onSent={onAgreementSent}
          hideAction={hidePanelAction}
        />
      </div>

      {/* Linked inspection */}
      {appointment.inspectionId && (
        <div className="mb-2">
          <p className="text-body-xs text-ink-subtle font-medium uppercase tracking-wide mb-1">Inspection</p>
          <button
            onClick={() => onViewInspection(appointment.inspectionId!)}
            className="text-body-sm text-brand hover:underline"
          >
            View linked inspection →
          </button>
        </div>
      )}

      {/* Feedback messages */}
      {confirmSuccess && (
        <div className="mt-3 p-3 bg-sev-safe-soft text-sev-safe-ink border border-sev-safe/20 rounded-md text-body-sm">
          Agreement sent to {appointment.clientEmail}. Inspection created and awaiting agreement signature.
        </div>
      )}
      {confirmError && (
        <div className="mt-3 p-3 bg-sev-minor-soft text-sev-minor-ink border border-sev-minor/20 rounded-md text-body-sm">
          {confirmError}
        </div>
      )}
    </Modal>
  );
};

export default Scheduling;
